// Mirrors gastronaut-api serverV03/Routes/NoShowProtection/domain/types.ts and
// services/types.ts. Amounts are always integer cents.

export const NO_SHOW_PROTECTION_APP_ID = 'noShowMollie';

export const DEFAULT_DEPOSIT_DEADLINE_MINUTES = 120;

export type FeeUnit = 'perPerson' | 'perGroup';

/** Applies when the change happens less than `hoursBefore` hours before the start; tiers descend, a no-show uses the last one. */
export type FeeTier = { hoursBefore: number; amountCents: number };

export type FeeTriggers = {
  fullCancellation: boolean;
  guestReduction: boolean;
  noShow: boolean;
};

export type TemplateConditions = {
  wDays?: number[];
  fromBlock?: number;
  tillBlock?: number;
  minGuests?: number;
  maxGuests?: number;
};

export type PaymentMethod = 'creditcard' | 'paypal';

export type NoShowTemplate = {
  id: string;
  name: string;
  active: boolean;
  version: number;
  scope: 'restaurant' | 'shifts';
  shiftIds?: string[];
  conditions?: TemplateConditions;
  unit: FeeUnit;
  tiers: FeeTier[];
  triggers: FeeTriggers;
  depositDeadlineMinutes?: number;
  extraText?: Record<string, string>;
};

export type NoShowAppSettings = {
  templates: NoShowTemplate[];
  methods: PaymentMethod[];
  notificationEmails?: string[];
  chargeUserIds?: string[];
  smsText?: string;
};

export type AgreedTerms = {
  templateId: string;
  templateVersion: number;
  templateName: string;
  unit: FeeUnit;
  tiers: FeeTier[];
  triggers: FeeTriggers;
  guestText: string;
  guests: number;
  startTs: number;
};

export type LiabilityKind = 'fullCancellation' | 'guestReduction' | 'noShow';

export type Liability = {
  id: string;
  kind: LiabilityKind;
  persons: number;
  occurredAt: number;
  capCents: number;
  recordedBy: string;
};

export type Observation = {
  id: string;
  kind: LiabilityKind;
  ts: number;
  persons: number;
  by: 'guest' | 'staff' | 'system';
  reviewed?: boolean;
};

export type ProtectionStatus =
  | 'pending'
  | 'secured'
  | 'expired'
  | 'waived'
  | 'failed';

export type NoShowProtection = {
  provider: 'mollie';
  status: ProtectionStatus;
  channel: 'widget' | 'staff';
  terms: AgreedTerms;
  deadline?: number | null;
  /** When the last deposit SMS was queued; queueing is not delivery. */
  smsSentAt?: number | null;
  lastSms?: { id: string; kind: 'request' | 'resend'; queuedAt: number } | null;
  consent?: {
    ts: number;
    guestText: string;
    channel: 'widget' | 'smsLink';
  } | null;
  mandate?: {
    method: PaymentMethod;
    mandateId: string | null;
    testmode: boolean;
  } | null;
  waived?: { by: string; ts: number; note?: string } | null;
  needsReconsent?: { reason: 'guests' | 'template'; ts: number } | null;
  pendingTerms?: AgreedTerms | null;
  observations: Observation[];
  liabilities: Liability[];
  totals: {
    requestedCents: number;
    collectedCents: number;
    refundedCents: number;
  };
  createdAt: number;
  updatedAt: number;
};

export type AttemptStatus =
  | 'creating'
  | 'open'
  | 'pending'
  | 'authorized'
  | 'paid'
  | 'failed'
  | 'canceled'
  | 'expired'
  | 'unknown'
  | 'abandoned';

export const IN_FLIGHT_ATTEMPT_STATUSES: AttemptStatus[] = [
  'creating',
  'open',
  'pending',
  'authorized',
  'unknown',
];

export type PaymentAttempt = {
  id: string;
  type: 'charge' | 'link';
  paymentId: string | null;
  status: AttemptStatus;
  checkoutUrl?: string | null;
  failureReason?: string | null;
  createdBy: string;
  createdAt: number;
};

export type ClaimStatus =
  | 'open'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'cancelled'
  | 'partially_refunded'
  | 'refunded'
  | 'charged_back';

export type ClaimRefund = {
  id: string;
  amountCents: number;
  status: string;
  createdBy: string;
  createdAt: number;
};

export type Claim = {
  id: string;
  restaurantId: string;
  reservationId: string;
  liabilityId: string;
  kind: LiabilityKind;
  persons: number;
  amountCents: number;
  message: string;
  status: ClaimStatus;
  attempts: PaymentAttempt[];
  paidCents: number;
  commissionCents: number;
  refundedCents: number;
  chargedBackCents: number;
  refunds: ClaimRefund[];
  /** Further paid payments of an already paid claim, to be refunded. */
  duplicatePayments?: { attemptId: string; paymentId: string; amountCents: number; detectedAt: number }[];
  /** Money confirmed after the claim was cancelled. */
  paidAfterCancel?: { attemptId: string; paymentId: string; amountCents: number; detectedAt: number } | null;
  /** The occasion collected more than its agreed cap across its claims. */
  exceedsCap?: { collectedCents: number; capCents: number; detectedAt: number } | null;
  receiptNumber?: string | null;
  paidAt?: number | null;
  testmode: boolean;
  guest: { name: string; email: string | null; phone: string | null };
  reservation: { date: string; time: string; guests: number; startTs: number };
  templateName: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
};

/**
 * What became of the last deposit SMS: queued, accepted by the SMS provider
 * (not proof of delivery to the handset), failed, or unclear.
 */
export type SmsDelivery = {
  kind: 'request' | 'resend';
  status: 'queued' | 'accepted' | 'failed' | 'unknown';
  queuedAt: number;
  at?: number | null;
  reason?: string | null;
};

/** GET /v03/noShowProtection/:rid/res/:reservationId */
export type ReservationProtectionResponse = {
  protection: NoShowProtection | null;
  smsDelivery?: SmsDelivery | null;
  claims: Claim[];
  matchingTemplate: { id: string; name: string; guestText: string; depositDeadlineMinutes: number } | null;
  canCharge: boolean;
};

export type OverviewReservation = {
  id: string;
  date: string;
  time: string;
  guests: number;
  status: string;
  noShow: boolean;
  guestName: string;
  protection: NoShowProtection;
};

/**
 * GET /v03/noShowProtection/:rid/overview: a page of protected reservations
 * (newest date first) and a page of pending deposits, with the claims of the
 * listed reservations; the cursors continue each list (null when complete).
 */
export type OverviewResponse = {
  reservations: OverviewReservation[];
  pending: OverviewReservation[];
  claims: Claim[];
  nextCursor: string | null;
  pendingCursor: string | null;
};
